/* global CustomEvent, document, window */

(() => {
  const STORAGE_KEY = 'idream.repairStatus.v2';
  const STORAGE_PROBE_KEY = 'idream.repairStatus.storageProbe';
  const STORAGE_VERSION = 2;
  const STATUS_CACHE_TTL_MS = 30 * 60 * 1000;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const ORDER_PATTERN = /^\d{1,12}$/;
  const DESKTOP_PROGRESS_BY_STAGE = {
    waiting_delivery: 0,
    queue: 30.51,
    in_service: 50,
    customer_action: 53.87,
    ready: 75,
    complete: 100,
  };
  const MOBILE_PROGRESS_BY_STAGE = {
    waiting_delivery: 8.85,
    queue: 31.82,
    in_service: 54.04,
    customer_action: 54.55,
    ready: 77.78,
    complete: 100,
  };

  const cleanString = (value, maxLength = 500) =>
    String(value ?? '')
      .trim()
      .slice(0, maxLength);

  const normalizeStatusType = (value) => cleanString(value, 100).toLowerCase();

  const parseStatusTypes = (value) =>
    String(value || '')
      .split(/[\n,]+/)
      .map(normalizeStatusType)
      .filter(Boolean);

  const createStatusPresentation = (steps, statusType) => {
    const normalizedType = normalizeStatusType(statusType);
    const matchedStep = steps.find((step) => step.statusTypes.includes(normalizedType));
    const showCustomerAction = matchedStep?.stage === 'customer_action';
    const visibleSteps = steps.filter((step) => {
      if (step.stage === 'customer_action') return showCustomerAction;
      if (step.stage === 'in_service') return !showCustomerAction;
      return true;
    });
    const currentIndex = matchedStep ? visibleSteps.indexOf(matchedStep) : -1;
    const logicalProgress = currentIndex < 0 ? 0 : (currentIndex / Math.max(visibleSteps.length - 1, 1)) * 100;
    const progress = matchedStep ? (DESKTOP_PROGRESS_BY_STAGE[matchedStep.stage] ?? logicalProgress) : 0;
    const mobileProgress = matchedStep ? (MOBILE_PROGRESS_BY_STAGE[matchedStep.stage] ?? logicalProgress) : 0;

    return {
      currentIndex,
      mobileProgress,
      progress,
      tone:
        matchedStep?.stage === 'complete'
          ? 'complete'
          : matchedStep?.stage === 'customer_action'
            ? 'action'
            : matchedStep
              ? 'progress'
              : 'unavailable',
      visibleSteps,
    };
  };

  const sanitizeResult = (value) => {
    if (!value || typeof value !== 'object') return null;

    const orderNumber = cleanString(value.orderNumber, 20);
    if (!orderNumber) return null;

    return {
      orderNumber,
      deviceName: cleanString(value.deviceName, 200),
      updatedAt: cleanString(value.updatedAt, 80),
      status: {
        type: normalizeStatusType(value.status?.type),
        description: cleanString(value.status?.description, 1000),
      },
    };
  };

  const createStoredResultRecord = (result, now = Date.now()) => {
    const sanitizedResult = sanitizeResult(result);
    if (!sanitizedResult) return null;

    return {
      version: STORAGE_VERSION,
      expiresAt: now + STATUS_CACHE_TTL_MS,
      result: sanitizedResult,
    };
  };

  const parseStoredResultRecord = (record, now = Date.now()) => {
    if (
      !record ||
      record.version !== STORAGE_VERSION ||
      !Number.isFinite(record.expiresAt) ||
      record.expiresAt <= now
    ) {
      return null;
    }

    return sanitizeResult(record.result);
  };

  const canUseSessionStorage = () => {
    try {
      window.sessionStorage.setItem(STORAGE_PROBE_KEY, '1');
      window.sessionStorage.removeItem(STORAGE_PROBE_KEY);
      return true;
    } catch {
      return false;
    }
  };

  window.IdreamRepairStatus = {
    STATUS_CACHE_TTL_MS,
    canUseSessionStorage,
    createStatusPresentation,
    createStoredResultRecord,
    normalizeStatusType,
    parseStatusTypes,
    parseStoredResultRecord,
    sanitizeResult,
  };

  const readStoredResult = () => {
    try {
      const storedValue = window.sessionStorage.getItem(STORAGE_KEY);
      if (!storedValue) return null;

      const result = parseStoredResultRecord(JSON.parse(storedValue));
      if (!result) window.sessionStorage.removeItem(STORAGE_KEY);
      return result;
    } catch {
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // Storage may be unavailable in privacy-restricted browser contexts.
      }
      return null;
    }
  };

  const storeResult = (result) => {
    try {
      const record = createStoredResultRecord(result);
      if (record) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // The current result is still dispatched when storage is unavailable.
    }
  };

  const readConfig = (root, selector) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || '{}');
    } catch {
      return {};
    }
  };

  const normalizePath = (value) => {
    try {
      const url = new window.URL(value, window.location.origin);
      return url.pathname.replace(/\/+$/, '') || '/';
    } catch {
      return '';
    }
  };

  const initializeDialog = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const config = readConfig(root, '[data-repair-status-dialog-config]');
    const panel = root.querySelector('[role="dialog"]');
    const form = root.querySelector('[data-repair-status-form]');
    const orderInput = form?.elements.orderNumber;
    const emailInput = form?.elements.email;
    const submit = root.querySelector('[data-repair-status-submit]');
    const submitLabel = root.querySelector('[data-repair-status-submit-label]');
    const loadingLabel = root.querySelector('[data-repair-status-loading-label]');
    const errorElement = root.querySelector('[data-repair-status-error]');
    const closeButtons = root.querySelectorAll('[data-repair-status-close]');
    const statusPageUrl = new window.URL(
      root.dataset.statusPageUrl || '/pages/service-portal-status-page',
      window.location.origin,
    );
    let returnFocus = null;
    let isLoading = false;

    if (!panel || !form || !orderInput || !emailInput || !submit || !errorElement) return;

    const isOrderValid = () => ORDER_PATTERN.test(orderInput.value.trim());
    const isEmailValid = () => EMAIL_PATTERN.test(emailInput.value.trim());

    const updateFieldState = () => {
      [orderInput, emailInput].forEach((input) => {
        input.closest('.idream-repair-status-dialog__field')?.classList.toggle('has-value', Boolean(input.value));
      });
      submit.disabled = isLoading || !isOrderValid() || !isEmailValid();
    };

    const showError = (message) => {
      errorElement.textContent = cleanString(message, 500) || config.genericError || 'Nie udało się sprawdzić statusu.';
      errorElement.hidden = false;
    };

    const clearError = () => {
      errorElement.hidden = true;
      errorElement.textContent = '';
    };

    const setLoading = (loading) => {
      isLoading = loading;
      submit.classList.toggle('is-loading', loading);
      submitLabel.hidden = loading;
      loadingLabel.hidden = !loading;
      updateFieldState();
    };

    const open = (trigger) => {
      returnFocus = trigger || document.activeElement;
      root.hidden = false;
      document.body.classList.add('idream-repair-status-modal-open');
      clearError();
      window.requestAnimationFrame(() => orderInput.focus());
    };

    const close = () => {
      if (isLoading) return;
      root.hidden = true;
      document.body.classList.remove('idream-repair-status-modal-open');
      if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus();
    };

    root.openRepairStatus = open;
    closeButtons.forEach((button) => button.addEventListener('click', close));
    [orderInput, emailInput].forEach((input) => input.addEventListener('input', updateFieldState));

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...panel.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable.at(-1);

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (isLoading) return;

      clearError();

      if (!isOrderValid()) {
        showError(config.invalidOrder);
        orderInput.focus();
        return;
      }

      if (!isEmailValid()) {
        showError(config.invalidEmail);
        emailInput.focus();
        return;
      }

      if (!window.ServiceRepairApi) {
        showError(config.genericError);
        return;
      }

      setLoading(true);

      try {
        const api = new window.ServiceRepairApi({ basePath: root.dataset.integrationBasePath });
        const result = sanitizeResult(
          await api.getRepairStatus({
            orderNumber: orderInput.value.trim(),
            email: emailInput.value.trim(),
          }),
        );

        if (!result) throw new Error('Invalid repair status response');

        storeResult(result);
        window.dispatchEvent(new CustomEvent('idream:repair-status-result', { detail: result }));

        if (normalizePath(window.location.href) !== normalizePath(statusPageUrl.href)) {
          setLoading(false);
          close();
          window.location.assign(statusPageUrl.href);
          return;
        }

        setLoading(false);
        close();
      } catch (error) {
        setLoading(false);
        const isPublicApiError =
          typeof window.ServiceRepairApiError === 'function' && error instanceof window.ServiceRepairApiError;
        showError(isPublicApiError ? error.message : config.genericError);
      }
    });

    if (!window.__idreamRepairStatusLinkBound) {
      window.__idreamRepairStatusLinkBound = true;
      document.addEventListener('click', (event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        const link = event.target.closest('a[href]');
        if (!link || link.target === '_blank') return;

        const target = new window.URL(link.href, window.location.origin);
        if (
          target.origin !== window.location.origin ||
          normalizePath(target.href) !== normalizePath(statusPageUrl.href)
        ) {
          return;
        }

        const isCurrentStatusPage = normalizePath(window.location.href) === normalizePath(statusPageUrl.href);
        if (!isCurrentStatusPage && readStoredResult()) return;
        if (!isCurrentStatusPage && !canUseSessionStorage()) return;

        const dialog = [...document.querySelectorAll('[data-repair-status-dialog]')].find(
          (candidate) => typeof candidate.openRepairStatus === 'function',
        );
        if (!dialog) return;

        event.preventDefault();
        dialog.openRepairStatus(link);
      });
    }

    updateFieldState();

    if (root.dataset.autoOpen === 'true' && !readStoredResult()) {
      window.setTimeout(() => open(), 0);
    }
  };

  const initializeResult = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const config = readConfig(root, '[data-repair-status-result-config]');
    const indicator = root.querySelector('[data-repair-status-indicator]');
    const orderNumber = root.querySelector('[data-repair-status-order-number]');
    const deviceName = root.querySelector('[data-repair-status-device]');
    const updated = root.querySelector('[data-repair-status-updated]');
    const description = root.querySelector('[data-repair-status-description]');
    const unavailable = root.querySelector('[data-repair-status-unavailable]');

    if (!indicator || !orderNumber || !deviceName || !updated || !description || !unavailable) return;

    const allStepElements = [...root.querySelectorAll('[data-status-stage]')];
    const steps = allStepElements.map((element) => ({
      element,
      stage: element.dataset.statusStage,
      statusTypes: parseStatusTypes(element.dataset.statusTypes),
    }));

    const render = (rawResult) => {
      const result = sanitizeResult(rawResult);
      if (!result) return;

      const presentation = createStatusPresentation(steps, result.status.type);
      const visibleElements = new Set(presentation.visibleSteps.map((step) => step.element));

      allStepElements.forEach((element) => {
        element.hidden = !visibleElements.has(element);
        element.classList.remove('is-current', 'is-complete');
      });

      presentation.visibleSteps.forEach((step, index) => {
        step.element.classList.toggle('is-current', index === presentation.currentIndex);
        step.element.classList.toggle('is-complete', index <= presentation.currentIndex);
      });

      indicator.dataset.tone = presentation.tone;
      indicator.style.setProperty('--status-progress', `${presentation.progress}%`);
      indicator.style.setProperty('--status-mobile-progress', `${presentation.mobileProgress}%`);
      unavailable.hidden = presentation.tone !== 'unavailable';
      unavailable.textContent = config.unavailable || unavailable.textContent;

      orderNumber.textContent = result.orderNumber;
      deviceName.textContent = result.deviceName || config.unknownDevice || 'Urządzenie Apple';
      updated.textContent = formatDate(result.updatedAt);
      description.textContent = result.status.description;
      description.hidden = !result.status.description;
      root.hidden = false;
    };

    const storedResult = readStoredResult();
    if (storedResult) render(storedResult);
    else if (root.dataset.editorPreview === 'true') {
      render({
        orderNumber: '00238829',
        deviceName: 'MacBook Pro',
        updatedAt: '2025-04-12T10:30:00+02:00',
        status: {
          type: root.dataset.editorPreviewStatus,
          description: 'Urządzenie jest obecnie obsługiwane przez serwis.',
        },
      });
    }
    window.addEventListener('idream:repair-status-result', (event) => render(event.detail));
  };

  const formatDate = (value) => {
    const sourceDate = cleanString(value, 80);
    const isoParts = sourceDate.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (isoParts) {
      const [, year, month, day, hour, minute, second = '00'] = isoParts;
      return `${day}-${month}-${year}, ${hour}:${minute}:${second}`;
    }

    const date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) return sourceDate;

    const parts = new Intl.DateTimeFormat('pl-PL', {
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      year: 'numeric',
    })
      .formatToParts(date)
      .reduce((result, part) => ({ ...result, [part.type]: part.value }), {});

    return `${parts.day}-${parts.month}-${parts.year}, ${parts.hour}:${parts.minute}:${parts.second}`;
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-repair-status-dialog]').forEach(initializeDialog);
    scope.querySelectorAll('[data-repair-status-result]').forEach(initializeResult);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
